import type { CentraSelectionItem } from "@frend-digital/centra-legacy/schemas/selection/selectionProductSchema";

import { getMediaObjects } from "./atomics/media";
import type { CentraProduct } from "./atomicSetup";
import { baseAtomics, VariantAvailability } from "./atomicSetup";
import formatProductCard from "./formatters/formatProductCards/formatProductCard";
import { formatVariant } from "./formatters/formatVariants";
import {
  getSelectionItemLine,
  getSelectionItemName,
  getSelectionItemPriceEach,
  getSelectionItemQuantity,
  getSelectionItemSize,
  getSelectionItemUri,
  getSelectionItemVariantName,
} from "./selectionAtoms";

const {
  getMediaBySize,
  getMeasurementChart,
  getProductBreadcrumbs,
  getProductSizes,
} = baseAtomics;

const formatMeasurementChart = (chart: {
  contents: { x: number; y: number; content: string }[];
  x: string[];
  y: string[];
}) => {
  const { contents, x, y } = chart;
  const formattedChart = [];

  for (let i = 0; i < y.length; i++) {
    const row = { size: y[i] } as {
      size: string;
      [measurement: string]: string;
    };
    for (let j = 0; j < x.length; j++) {
      const measurement = x[j];
      const value = contents.find(
        (content: { x: number; y: number }) =>
          content.x === j && content.y === i,
      )?.content;
      if (value) {
        row[measurement] = value;
      }
    }
    formattedChart.push(row);
  }

  return { chart: formattedChart, keys: x };
};

export const getSizeGuide = (product: CentraProduct) => {
  const measurementChart = getMeasurementChart(product);
  const sizeGuide = formatMeasurementChart(measurementChart);
  return sizeGuide;
};

export const getComingSoon = (product: CentraProduct) => {
  const availability = getAvailability(product);

  return availability === VariantAvailability.ComingSoon;
};

export const getSoldOut = (product: CentraProduct) => {
  const sizes = getProductSizes(product);
  const soldOut = sizes?.every(size => size.stock === false);

  return soldOut;
};

export const getSwatch = (product: CentraProduct) => {
  const swatch = product.pr_swatch;
  return swatch;
};

export const getCare = (product: CentraProduct) => {
  const care = product.pr_care;
  return care;
};

export const getLabels = (product: CentraProduct) => {
  const labels: {
    labelType: string;
    value: string;
  }[] = [];

  if (product.pr_gender) {
    labels.push({
      labelType: "gender",
      value: product.pr_gender,
    });
  } else {
    labels.push({
      labelType: "gender",
      value: "unisex",
    });
  }
  if (product.var_availability) {
    labels.push({
      labelType: "availability",
      value: getAvailability(product),
    });
  }

  return labels;
};

export const getAvailability = (product: CentraProduct) => {
  const availability =
    product.var_availability || VariantAvailability.Available;

  return availability;
};

export const getModelDisclaimer = (product: CentraProduct) => {
  return product.dis_model_disclaimer_value;
};

export const getFitAdvice = (product: CentraProduct) => {
  const fit = product.pr_fit_advice_value;
  return fit;
};

export const getMaterialCare = (product: CentraProduct) => {
  const materialCare = product.pr_material_care_value;
  return materialCare;
};

export const getFaq = (product: CentraProduct) => {
  const faq = product.pr_faq;
  return faq;
};

export const getShortDesc = (product: CentraProduct) => {
  const desc = product.excerptHtml;
  return desc;
};

export const getFabricProps = (product: CentraProduct) => {
  const fabricProps = product.pr_fabric_props;
  return fabricProps;
};

export const getMedia = (product: CentraProduct) => {
  const objects = getMediaObjects(product, "full");

  return objects;
};

const getRelatedProducts = (product: CentraProduct) => {
  return product.relatedProducts;
};
export const getSpecs = (product: CentraProduct) => {
  return {
    name: product.name,
    sku: product.sku,
    collection: product.collection,
    variant: product.variantName,
  };
};

export const getRelation = (
  product: CentraProduct,
  relation: string,
  includeSelf: boolean,
) => {
  const relatedProducts = getRelatedProducts(product) as CentraProduct[];
  if (Array.isArray(relatedProducts)) {
    const inactiveVariants = relatedProducts
      ?.filter(product => product.relation === relation)
      .map(product => {
        return {
          ...formatVariant(product).data,
          selected: false,
        };
      });
    let variants;
    if (includeSelf) {
      const activeVariant = { ...formatVariant(product).data, selected: true };
      variants = inactiveVariants
        ? [activeVariant, ...inactiveVariants]
        : [activeVariant];
    } else {
      variants = inactiveVariants;
    }

    return variants.sort((a, b) => {
      if ((a.uri ?? "") > (b.uri ?? "")) return 1;
      return -1;
    });
  }
  return [];
};

export const getVariants = (product: CentraProduct) => {
  return getRelation(product, "variant", true);
};
export type ProductVariant = ReturnType<typeof getVariants>[number];

export const getGender = (product: CentraProduct): string => {
  return product.pr_gender;
};

export const getDisplayItemId = (product: CentraProduct): string => {
  return product.product;
};

export const getCategories = (product: CentraProduct) => {
  return product.categories;
};

export const getGenders = (product: CentraProduct) => {
  return getRelation(product, "gender", false);
};

export const getRelatedProductCards = (product: CentraProduct) => {
  const data = getRelatedProducts(product) as CentraProduct[];
  if (!Array.isArray(data)) return [];
  const relatedProducts = data?.filter(
    product => product.relation !== "variant",
  );
  return relatedProducts.map(product => formatProductCard(product));
};
export type RelatedProductCards = ReturnType<typeof getRelatedProductCards>;

export const getFirstImage = (
  item: CentraSelectionItem,
): {
  standard: {
    href: string;
    alt: string;
  };
  full: {
    href: string;
    alt: string;
  };
} => {
  const mediaData = Object.keys(item?.product?.media);

  let media = {} as {
    standard: {
      href: string;
      alt: string;
    };
    full: {
      href: string;
      alt: string;
    };
  };
  mediaData.forEach(key => {
    if (key === "standard") {
      media.standard = { href: item?.product?.media[key]?.[0], alt: "" };
    }
    if (key === "full") {
      media.full = { href: item?.product?.media[key]?.[0], alt: "" };
    }
  });
  return media;
};

export const generateCartItem = (item: CentraSelectionItem) => {
  const name = getSelectionItemName(item) ?? "";
  const uri = getSelectionItemUri(item) ?? "";
  const variant = getSelectionItemVariantName(item) ?? "";
  const size = getSelectionItemSize(item) ?? "";
  const id = getSelectionItemLine(item) ?? "";
  const price = getSelectionItemPriceEach(item);
  const discountedPrice = getSelectionItemPriceEach(item);
  const quantity = getSelectionItemQuantity(item) ?? 0;
  const media = getFirstImage(item);
  const line = item.line ?? "";
  return {
    name,
    uri,
    variant,
    size,
    id,
    price,
    quantity,
    media,
    discountedPrice,
    line,
  };
};

export type GeneratedCartItem = ReturnType<typeof generateCartItem>;

/**
 * Related Products is an array of related products with all variants denormalized.
 * Now we must group them by productSku and set relatedProducts to the array of variants
 */
export const createRelationsFromDisplays = (
  relatedProducts: CentraProduct[],
) => {
  const productsGroupedByRelation = relatedProducts.reduce((acc, product) => {
    const productSku = product.productSku;
    if (!acc.has(productSku)) {
      acc.set(productSku, {
        ...product,
        relatedProducts: [],
      });
    }

    (acc.get(productSku)?.relatedProducts as CentraProduct[])?.push({
      ...product,
      relation: "variant",
    });
    return acc;
  }, new Map<string, CentraProduct>());

  return Array.from(productsGroupedByRelation.values());
};

export const getStandardRelatedProducts = (item: CentraProduct) => {
  const standardRelatedProducts = (
    item.relatedProducts as CentraProduct[]
  ).filter(product => product.relation === "standard");

  return standardRelatedProducts.map(product => formatProductCard(product));
};

export const getGenderAlternative = (item: CentraProduct) => {
  const standardRelatedProducts = (
    item.relatedProducts as CentraProduct[]
  ).filter(product => product.relation === "Gender");
  if (!standardRelatedProducts?.[0]) {
    return undefined;
  }
  return formatProductCard(standardRelatedProducts?.[0]);
};

export const getIsNew = (item: CentraProduct) => {
  if ("pr_new" in item) return item.pr_new as "1";
  else return;
};
